import { createRouter, createWebHistory } from 'vue-router';
import store from '../store';

const routes = [
  {
    path: '/',
    name: 'root',
    component: () => import('../views/RootView.vue'),
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('../views/HomeView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/welcome',
    name: 'welcome',
    component: () => import('../views/WelcomeView.vue'),
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/RegisterView.vue'),
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/ForgotPasswordView.vue'),
  },
  {
    path: '/terms-and-conditions',
    name: 'terms-and-conditions',
    component: () => import('../views/TermsAndConditionsView.vue'),
  },
  {
    path: '/projects',
    name: 'projects',
    component: () => import('../views/ProjectsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/projects/new',
    name: 'project-new',
    component: () => import('../views/ProjectFormView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'supervisor'] },
  },
  {
    path: '/projects/:id',
    name: 'project-detail',
    component: () => import('../views/ProjectDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/projects/:id/edit',
    name: 'project-edit',
    component: () => import('../views/ProjectFormView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'supervisor'] },
  },
  {
    path: '/records',
    name: 'records',
    component: () => import('../views/RecordsListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/records/new',
    name: 'record-new',
    component: () => import('../views/RecordFormView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'supervisor'] },
  },
  {
    path: '/records/:id',
    name: 'record-detail',
    component: () => import('../views/RecordDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/records/:id/edit',
    name: 'record-edit',
    component: () => import('../views/RecordFormView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'supervisor'] },
  },
  {
    path: '/buildings',
    name: 'buildings',
    component: () => import('../views/BuildingsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/floors',
    name: 'floors',
    component: () => import('../views/FloorsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/rooms',
    name: 'rooms',
    component: () => import('../views/RoomsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports',
    name: 'reports',
    component: () => import('../views/ReportsListView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'supervisor'] },
  },
  {
    path: '/reports/summary',
    name: 'reports-summary',
    component: () => import('../views/ReportsSummaryView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'supervisor'] },
  },
  {
    path: '/reports/records',
    name: 'reports-records',
    component: () => import('../views/RecordsReportView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'supervisor'] },
  },
  {
    path: '/reports/projects',
    name: 'reports-projects',
    component: () => import('../views/ProjectsReportView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'supervisor'] },
  },
  {
    path: '/reports/time',
    name: 'reports-time',
    component: () => import('../views/TimeReportView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'supervisor'] },
  },
  {
    path: '/reports/users',
    name: 'reports-users',
    component: () => import('../views/UsersReportView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'supervisor'] },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../views/ProfileView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('../views/SettingsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('../views/admin/AdminUsersView.vue'),
    meta: { requiresAuth: true, roles: ['admin'] },
  },
  {
    path: '/admin/users/create',
    name: 'admin-users-create',
    component: () => import('../views/admin/AdminCreateUserView.vue'),
    meta: { requiresAuth: true, roles: ['admin'] },
  },
  {
    path: '/admin/users/:id',
    name: 'admin-user-detail',
    component: () => import('../views/admin/AdminUserDetailView.vue'),
    meta: { requiresAuth: true, roles: ['admin'] },
  },
  {
    path: '/admin/supervisors',
    name: 'admin-supervisors',
    component: () => import('../views/admin/AdminSupervisorsView.vue'),
    meta: { requiresAuth: true, roles: ['admin'] },
  },
  {
    path: '/admin/supervisors/create',
    name: 'admin-supervisors-create',
    component: () => import('../views/admin/AdminCreateSupervisorView.vue'),
    meta: { requiresAuth: true, roles: ['admin'] },
  },
  {
    path: '/supervisor/users',
    name: 'supervisor-users',
    component: () => import('../views/supervisor/SupervisorUsersView.vue'),
    meta: { requiresAuth: true, roles: ['supervisor'] },
  },
  {
    path: '/supervisor/users/create',
    name: 'supervisor-users-create',
    component: () => import('../views/supervisor/SupervisorCreateUserView.vue'),
    meta: { requiresAuth: true, roles: ['supervisor'] },
  },
  {
    path: '/supervisor/users/:id',
    name: 'supervisor-user-statistics',
    component: () => import('../views/supervisor/SupervisorUserStatisticsView.vue'),
    meta: { requiresAuth: true, roles: ['supervisor'] },
  },
  {
    path: '/supervisor/statistics',
    name: 'supervisor-statistics',
    component: () => import('../views/supervisor/SupervisorStatsView.vue'),
    meta: { requiresAuth: true, roles: ['supervisor'] },
  },
];

// Ensure BASE_URL is set correctly (default to '/' for production)
const baseUrl = process.env.BASE_URL || '/';

const router = createRouter({
  history: createWebHistory(baseUrl),
  routes,
});

router.beforeEach(async (to, from, next) => {
  // Initialize auth if not already done (for root route)
  if (to.name === 'root' && !store.getters['auth/isAuthenticated']) {
    try {
      await store.dispatch('auth/init');
    } catch (error) {
      // Auth init failed, continue to welcome
    }
  }

  const isAuthenticated = store.getters['auth/isAuthenticated'];
  const userRole = store.getters['auth/userRole'];

  // Root route handles its own routing logic
  if (to.name === 'root') {
    next();
    return;
  }

  if (to.meta.requiresAuth && !isAuthenticated) {
    next({ name: 'welcome', query: { redirect: to.fullPath } });
    return;
  }

  // Redirect authenticated users away from auth pages
  if ((to.name === 'login' || to.name === 'register' || to.name === 'welcome' || to.name === 'forgot-password') && isAuthenticated) {
    next({ name: 'home' });
    return;
  }

  // Redirect unauthenticated users from root to welcome
  if (to.name === 'root' && !isAuthenticated) {
    // RootView will handle showing welcome, just continue
    next();
    return;
  }

  if (to.meta.roles && !to.meta.roles.includes(userRole)) {
    next({ name: 'home' });
    return;
  }

  // Enforce hierarchy: cannot access inner units without parent context (project → buildings → floors → rooms → records)
  const q = to.query || {};

  if (to.name === 'buildings') {
    if (!q.project) {
      next({ name: 'projects' });
      return;
    }
  }

  if (to.name === 'floors') {
    if (!q.project || !q.building) {
      if (q.project) {
        next({
          name: 'buildings',
          query: {
            project: q.project,
            projectName: q.projectName,
            projectDepth: q.projectDepth,
          },
        });
      } else {
        next({ name: 'projects' });
      }
      return;
    }
  }

  if (to.name === 'rooms') {
    if (!q.project || !q.building || !q.floor) {
      if (q.building && q.project) {
        next({
          name: 'floors',
          query: {
            project: q.project,
            projectName: q.projectName,
            building: q.building,
            buildingName: q.buildingName,
            projectDepth: q.projectDepth,
          },
        });
      } else if (q.project) {
        next({
          name: 'buildings',
          query: {
            project: q.project,
            projectName: q.projectName,
            projectDepth: q.projectDepth,
          },
        });
      } else {
        next({ name: 'projects' });
      }
      return;
    }
  }

  if (to.name === 'records') {
    if (!q.project) {
      next({ name: 'projects' });
      return;
    }
  }

  if (to.name === 'record-new') {
    if (!q.project) {
      next({ name: 'projects' });
      return;
    }
  }

  next();
});

export default router;


